import { useEffect, useState } from 'react'
import { useLanguage } from './LanguageContext'
import { useAuth } from '../auth/AuthContext'

function formatFreshnessText(lastSuccessAt, language, t, now) {
  if (!lastSuccessAt) {
    return `${t('lastUpdated')}${language === 'cn' ? '：' : ': '}${t('lastUpdatedUnknown')}`
  }

  const parsed = new Date(lastSuccessAt)
  if (Number.isNaN(parsed.getTime())) {
    return `${t('lastUpdated')}${language === 'cn' ? '：' : ': '}${t('lastUpdatedUnknown')}`
  }

  const ageMs = Math.max(0, now - parsed.getTime())
  let relative = t('updatedJustNow')
  if (ageMs >= 60 * 60 * 1000) {
    const hours = Math.floor(ageMs / (60 * 60 * 1000))
    relative = language === 'cn'
      ? `${hours} 小时前`
      : `${hours} hr${hours === 1 ? '' : 's'} ago`
  } else if (ageMs >= 60 * 1000) {
    const minutes = Math.floor(ageMs / (60 * 1000))
    relative = language === 'cn'
      ? `${minutes} 分钟前`
      : `${minutes} min${minutes === 1 ? '' : 's'} ago`
  }

  return `${t('lastUpdated')}${language === 'cn' ? '：' : ': '}${relative}`
}

export default function PortalHeader({ studentName, freshness }) {
  const { t, language, toggleLanguage } = useLanguage()
  // Sign Out was removed 2026-08-12 by operator order (#459 re-scope). A
  // portal session IS its tokenized link — signing out only clears local
  // state, so the button was security theater whose one observed effect was
  // a mis-tap next to the language toggle that read as a page crash.
  // `AuthContext.signOut` itself is deliberately retained: it is still the
  // canonical local-state reset and keeps its own pinned test suite.
  const { userType, role } = useAuth()
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now())
    }, 60 * 1000)
    return () => window.clearInterval(timer)
  }, [])
  const portalTitle = userType === 'parent'
    ? t('parentPortal')
    : role === 'ta'
      ? t('taPortal')
      : t('studentPortal')
  const displayName = studentName || portalTitle
  const initial = displayName ? displayName.charAt(0).toUpperCase() : '?'
  const freshnessSection = freshness && typeof freshness === 'object' && Object.prototype.hasOwnProperty.call(freshness, 'data')
    ? freshness
    : null
  const freshnessData = freshnessSection ? freshnessSection.data : freshness
  const freshnessText = freshnessData
    ? formatFreshnessText(freshnessData.lastSuccessAt, language, t, now)
    : `${t('lastUpdated')}${language === 'cn' ? '：' : ': '}${t('lastUpdatedUnknown')}`
  const ageMs = freshnessData?.lastSuccessAt
    ? Math.max(0, now - new Date(freshnessData.lastSuccessAt).getTime())
    : null
  const isStale = ageMs != null && !Number.isNaN(ageMs) && ageMs > 10 * 60 * 1000
  const hasSyncWarning = freshnessData?.status === 'warning' || freshnessData?.status === 'error'
  const freshnessClass = isStale || hasSyncWarning ? 'warning' : 'ok'

  return (
    <header className="portal-header">
      <div className="portal-header-left">
        <div className="portal-header-avatar">{initial}</div>
        <div className="portal-header-info">
          <h1 className="portal-header-title">{displayName}</h1>
          {studentName && (
            <span className="portal-welcome">{t('welcomeBack')} {studentName}.</span>
          )}
          {freshness && (
            <div className={`portal-freshness portal-freshness-${freshnessClass}`}>
              <span className="portal-freshness-badge">{freshnessText}</span>
              {(isStale || hasSyncWarning) && (
                <span className="portal-freshness-note">{t('dataMayBeDelayed')}</span>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="portal-header-right">
        <button
          className="lang-toggle"
          onClick={toggleLanguage}
          aria-label={`Switch to ${language === 'en' ? 'Chinese' : 'English'}`}
        >
          {t('language')}
        </button>
      </div>
    </header>
  )
}
